import React, { useMemo } from 'react';
import { Box, Grid, FormLabel, Typography, TextField, Button, Alert, InputAdornment } from '@mui/material';
import NumberFormatCustom from '../common/form/NumberFormatCustom';

const YearGoalForm = ({ formik }) => {
    const defaultRatios = [0.05, 0.05, 0.05, 0.05, 0.05, 0.15, 0.05, 0.10, 0.10, 0.10, 0.10, 0.15];

    const formatDiff = (num) => {
        return new Intl.NumberFormat('ko-KR', { signDisplay: 'always' }).format(num);
    };

    const getDiffStatus = (diff) => {
    if (diff > 0) return { label: '차액 부족', color: 'primary.main' };
    if (diff < 0) return { label: '차액 초과', color: 'error.main' };
        return { label: '정확히 일치', color: 'success.main' };
    };

    const stats = useMemo(() => {
        const totalSales = Number(formik.values.ptargetSales || 0);
        const totalProfit = Number(formik.values.ptargetSalesProfit || 0);
        const totalOpProfit = Number(formik.values.ptargetOperatingProfit || 0); // 추가

        const salesSum = formik.values.monthData?.reduce((acc, cur) => acc + Number(cur.sales || 0), 0) || 0;
        const profitSum = formik.values.monthData?.reduce((acc, cur) => acc + Number(cur.profit || 0), 0) || 0;
        const opProfitSum = formik.values.monthData?.reduce((acc, cur) => acc + Number(cur.operatingProfit || 0), 0) || 0; // 추가
        
        const currentRatios = formik.values.monthData?.map(month => {
            if (!totalSales || !month.sales) return "0.0";
            return ((Number(month.sales) / totalSales) * 100).toFixed(1);
        }) || Array(12).fill("0.0");

        return {
            salesDiff: totalSales - salesSum,
            profitDiff: totalProfit - profitSum,
            opProfitDiff: totalOpProfit - opProfitSum, // 추가
            currentRatios,
            isAutoDistributeEnabled: Boolean(totalSales) && Boolean(totalProfit) && Boolean(totalOpProfit)
        };
    }, [formik.values]);

    const handleAutoDistribute = (type) => {
        const currentMonthData = [...formik.values.monthData];
        
        const updated = currentMonthData.map((data, index) => {
            const ratio = defaultRatios[index];
            const newItem = { ...data };

            if (type === 'sales') {
                const totalSales = Number(formik.values.ptargetSales || 0);
                newItem.sales = Math.round(totalSales * ratio);
            } else if (type === 'profit') {
                const totalProfit = Number(formik.values.ptargetSalesProfit || 0);
                newItem.profit = Math.round(totalProfit * ratio);
            } else if (type === 'operatingProfit') {
                const totalOpProfit = Number(formik.values.ptargetOperatingProfit || 0);
                newItem.operatingProfit = Math.round(totalOpProfit * ratio);
            }

            return newItem;
        });

        formik.setFieldValue('monthData', updated);
    };

        const rowStyle = {
            px: 1, py: 0.2, 
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            width: '100%', 
            mx: 0,
            '&:hover': { bgcolor: 'action.hover' }
        };

    return (
        <Box component="form" noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
           {!stats.isAutoDistributeEnabled && (
                <Alert severity="info" sx={{ mb: 2, fontSize: '0.8rem', py: 0 }}>
                    상단의 <b>총목표매출</b>, <b>총매출이익</b>, <b>총영업이익</b>을 모두 입력하시면 월별 자동 배분이 활성화됩니다.
                </Alert>
            )}

            <Box sx={{ width: '100%' }}>
                <Grid container alignItems="flex-start" spacing={2} sx={{ px: 2, width: '100%', mx: 0 }}>
                    <Grid item xs={4} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <FormLabel sx={{ pt: 1, minWidth: '70px' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', textAlign: 'right' }}>총매출</Typography>
                        </FormLabel>
                        <Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
                            <TextField
                                size="small" fullWidth name="ptargetSales"
                                placeholder="총매출 입력"
                                value={formik.values.ptargetSales || ''}
                                onChange={formik.handleChange}
                                InputProps={{ inputComponent: NumberFormatCustom, sx: { fontSize: '0.9rem', height: '40px' } }}
                                error={Boolean(formik.values.ptargetSales) && stats.salesDiff !== 0}
                                helperText={
                                    <Typography component="span" sx={{ fontSize: '0.75rem', color: formik.values.ptargetSales ? getDiffStatus(stats.salesDiff).color : 'text.secondary' }}>
                                        {formik.values.ptargetSales ? (stats.salesDiff === 0 ? '일치' : formatDiff(stats.salesDiff)) : "매출액 입력"}
                                    </Typography>
                                }
                                FormHelperTextProps={{ sx: { m: 0, mt: 0.5 } }}
                            />
                            <Button 
                                variant="outlined" color="secondary" 
                                onClick={() => handleAutoDistribute('sales')}
                                disabled={!formik.values.ptargetSales}
                                sx={{ minWidth: '60px', height: '40px', fontWeight: 'bold', whiteSpace: 'nowrap' }}
                            >
                                배분
                            </Button>
                        </Box>
                    </Grid>
                    <Grid item xs={4} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <FormLabel sx={{ pt: 1, minWidth: '85px' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', textAlign: 'right' }}>총매출이익</Typography>
                        </FormLabel>
                        <Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
                            <TextField
                                size="small" fullWidth name="ptargetSalesProfit"
                                placeholder="총매출이익 입력"
                                value={formik.values.ptargetSalesProfit || ''}
                                onChange={formik.handleChange}
                                InputProps={{ inputComponent: NumberFormatCustom, sx: { fontSize: '0.9rem', height: '40px' } }}
                                error={Boolean(formik.values.ptargetSalesProfit) && stats.profitDiff !== 0}
                                helperText={
                                    <Typography component="span" sx={{ fontSize: '0.75rem', color: formik.values.ptargetSalesProfit ? getDiffStatus(stats.profitDiff).color : 'text.secondary' }}>
                                        {formik.values.ptargetSalesProfit ? (stats.profitDiff === 0 ? '일치' : formatDiff(stats.profitDiff)) : "이익액 입력"}
                                    </Typography>
                                }
                                FormHelperTextProps={{ sx: { m: 0, mt: 0.5 } }}
                            />
                            <Button 
                                variant="outlined" color="secondary" 
                                onClick={() => handleAutoDistribute('profit')} 
                                disabled={!formik.values.ptargetSalesProfit}
                                sx={{ minWidth: '60px', height: '40px', fontWeight: 'bold', whiteSpace: 'nowrap' }}
                            >
                                배분
                            </Button>
                        </Box>
                    </Grid>

                    {/* 3. 총영업이익 */}
                    <Grid item xs={4} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <FormLabel sx={{ pt: 1, minWidth: '85px' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', textAlign: 'right' }}>총영업이익</Typography>
                        </FormLabel>
                        <Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
                            <TextField
                                size="small" fullWidth name="ptargetOperatingProfit"
                                placeholder="총영업이익 입력"
                                value={formik.values.ptargetOperatingProfit || ''}
                                onChange={formik.handleChange}
                                InputProps={{ inputComponent: NumberFormatCustom, sx: { fontSize: '0.9rem', height: '40px' } }}                       
                                error={Boolean(formik.values.ptargetOperatingProfit) && stats.opProfitDiff !== 0}
                                helperText={
                                    <Typography component="span" sx={{ fontSize: '0.75rem', color: formik.values.ptargetOperatingProfit ? getDiffStatus(stats.opProfitDiff).color : 'text.secondary' }}>
                                        {formik.values.ptargetOperatingProfit ? (stats.opProfitDiff === 0 ? '일치' : formatDiff(stats.opProfitDiff)) : "영업이익 입력"}
                                    </Typography>
                                }
                                FormHelperTextProps={{ sx: { m: 0, mt: 0.5 } }}
                            />
                            <Button 
                                variant="outlined" color="secondary" 
                                onClick={() => handleAutoDistribute('operatingProfit')} 
                                disabled={!formik.values.ptargetOperatingProfit}
                                sx={{ minWidth: '60px', height: '40px', fontWeight: 'bold', whiteSpace: 'nowrap' }}
                            >
                                배분
                            </Button>
                        </Box>
                    </Grid>
                </Grid>

                <Grid container spacing={1} sx={{ px: 1, py: 1.5, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
                    <Grid item xs={6}>
                        {stats.salesDiff === 0 && stats.profitDiff === 0 && stats.opProfitDiff === 0 && formik.values.ptargetSales && (
                            <Typography variant="caption" color="success.main" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                                ✓ 모든 항목의 월별 배분이 완료되었습니다.
                            </Typography>
                        )}
                    </Grid>
                </Grid>
            </Box>


            <Typography variant="caption" sx={{ px: 2, py: 1, display: 'block', color: 'text.secondary' }}>
                ※ 자동배분 후 각 월별 수치를 수동으로 수정할 수 있습니다. (비중 % 자동 계산)
            </Typography>
            <Grid container spacing={1} sx={{ px: 2, mb: 1, textAlign: 'center' }}>
                <Grid item xs={3} />
                <Grid item xs={3}><Typography sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '0.95rem' }}>매출 목표</Typography></Grid>
                <Grid item xs={3}><Typography sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '0.95rem' }}>매출 이익 목표</Typography></Grid>
                <Grid item xs={3}><Typography sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '0.95rem' }}>영업 이익 목표</Typography></Grid>
            </Grid>

            {formik.values.monthData?.map((_, idx) => (
                <Grid key={idx} container alignItems="center" spacing={1} sx={rowStyle}>
                    <Grid item xs={3} align="left">
                        <Box sx={{ ml: 2, display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.85rem', minWidth: '35px' }}>
                                {idx + 1}월
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                (자동 배분 비율:{Math.round(defaultRatios[idx] * 100)}%)
                            </Typography>
                        </Box>
                    </Grid>

                    {/* 1. 매출 */}
                    <Grid item xs={3}>
                        <TextField
                            size="small" fullWidth
                            name={`monthData[${idx}].sales`}
                            value={formik.values.monthData[idx].sales || '0'} 
                            onChange={formik.handleChange}
                            inputProps={{ style: { textAlign: 'right' } }}
                            InputProps={{ 
                                inputComponent: NumberFormatCustom, 
                                sx: { fontSize: '0.85rem' },
                                endAdornment: (
                                    <InputAdornment position="end" sx={{ ml: 1 }}>
                                        {/* 세로 구분선 */}
                                        <Box sx={{ width: '1px', height: '16px', bgcolor: 'divider', mr: 1 }} />
                                        <Typography sx={{ fontSize: '0.7rem', color: 'primary.main', fontWeight: 600, minWidth: '35px', textAlign: 'right' }}>
                                            {(() => {
                                                const total = Number(formik.values.ptargetSales) || 0;
                                                const current = Number(formik.values.monthData[idx].sales) || 0;
                                                return total > 0 ? `${((current / total) * 100).toFixed(1)}%` : '0%';
                                            })()}
                                        </Typography>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>

                    {/* 2. 매출이익 */}
                    <Grid item xs={3}>
                        <TextField
                            size="small" fullWidth
                            name={`monthData[${idx}].profit`}
                            value={formik.values.monthData[idx].profit || '0'}
                            onChange={formik.handleChange}
                            inputProps={{ style: { textAlign: 'right' } }}
                            InputProps={{ 
                                inputComponent: NumberFormatCustom, 
                                sx: { fontSize: '0.85rem' },
                                endAdornment: (
                                    <InputAdornment position="end" sx={{ ml: 1 }}>
                                        <Box sx={{ width: '1px', height: '16px', bgcolor: 'divider', mr: 1 }} />
                                        <Typography sx={{ fontSize: '0.7rem', color: 'primary.main', fontWeight: 600, minWidth: '35px', textAlign: 'right' }}>
                                            {(() => {
                                                const total = Number(formik.values.ptargetSalesProfit) || 0;
                                                const current = Number(formik.values.monthData[idx].profit) || 0;
                                                return total > 0 ? `${((current / total) * 100).toFixed(1)}%` : '0%';
                                            })()}
                                        </Typography>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>

                    {/* 3. 영업이익 */}
                    <Grid item xs={3}>
                        <TextField
                            size="small" fullWidth
                            name={`monthData[${idx}].operatingProfit`}
                            value={formik.values.monthData[idx].operatingProfit || '0'}
                            onChange={formik.handleChange}
                            inputProps={{ style: { textAlign: 'right' } }}
                            InputProps={{ 
                                inputComponent: NumberFormatCustom, 
                                sx: { fontSize: '0.85rem' },
                                endAdornment: (
                                    <InputAdornment position="end" sx={{ ml: 1 }}>
                                        <Box sx={{ width: '1px', height: '16px', bgcolor: 'divider', mr: 1 }} />
                                        <Typography sx={{ fontSize: '0.7rem', color: 'primary.main', fontWeight: 600, minWidth: '35px', textAlign: 'right' }}>
                                            {(() => {
                                                const total = Number(formik.values.ptargetOperatingProfit) || 0;
                                                const current = Number(formik.values.monthData[idx].operatingProfit) || 0;
                                                return total > 0 ? `${((current / total) * 100).toFixed(1)}%` : '0%';
                                            })()}
                                        </Typography>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                </Grid>
            ))}
            <Grid container spacing={1} sx={{ px: 1, py: 1, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <Button 
                        type="submit" 
                        variant="contained" 
                        disabled={
                            formik.isSubmitting || 
                            !formik.values.ptargetSales || 
                            stats.salesDiff !== 0 || 
                            stats.profitDiff !== 0 || 
                            stats.opProfitDiff !== 0
                        } 
                        sx={{ minWidth: '120px', fontWeight: 'bold' }}
                    >
                        최종 저장
                    </Button>
                </Grid>
            </Grid>
        </Box>
    );
};

export default YearGoalForm;
